import type { NurseWithRelation } from "@/types/nurse-type";
import { useMemo } from "react";
import { SelectItem } from "@/components/ui/select";
import type { TeamsSchema } from "@/hooks/roster-craete/useTeamsQuery";
import {
  NurseSwapTables,
  type BucketPillOption,
} from "@/components/feature/nurse-swap/NurseSwapTables";
import { sortTeamsByName } from "@/utils/team-sort-util";

interface TeamTransferTablesProps {
  leftTableData: NurseWithRelation[];
  rightTableData: NurseWithRelation[];
  leftTableTitle?: string;
  rightTableTitle?: string;
  selectedGroupId: string | null;
  activeNurseIdForSideProfile?: string | null;
  onNurseClick?: (nurseId: string) => void;
  teams: TeamsSchema;
  nursesByTeam: Record<number, NurseWithRelation[]>;
  unassignedNurses: NurseWithRelation[];
  invalidTeamTooltip?: (teamId: number | "unassigned") => string | undefined;
  selectedLeftTeamId: number | "unassigned" | null;
  selectedRightTeamId: number | "unassigned" | null;
  onLeftTeamChange: (teamId: number | "unassigned" | null) => void;
  onRightTeamChange: (teamId: number | "unassigned" | null) => void;
  onMoveNurses?: (
    nurseIds: string[],
    fromTeamId: number | "unassigned" | null,
    toTeamId: number | "unassigned" | null
  ) => void;
}

export function TeamTransferTables({
  leftTableData,
  rightTableData,
  leftTableTitle = "미지정",
  rightTableTitle = "팀 멤버",
  selectedGroupId,
  activeNurseIdForSideProfile,
  onNurseClick,
  teams,
  nursesByTeam,
  unassignedNurses,
  invalidTeamTooltip,
  selectedLeftTeamId,
  selectedRightTeamId,
  onLeftTeamChange,
  onRightTeamChange,
  onMoveNurses,
}: TeamTransferTablesProps) {
  const sortedTeams = useMemo(
    () => sortTeamsByName(teams),
    [teams]
  );

  const sameBucketSelected = useMemo(() => {
    if (selectedRightTeamId === null) return false;
    if (
      selectedRightTeamId === "unassigned" &&
      (selectedLeftTeamId === "unassigned" || selectedLeftTeamId === null)
    ) {
      return true;
    }
    if (
      typeof selectedRightTeamId === "number" &&
      selectedLeftTeamId !== null &&
      selectedLeftTeamId !== "unassigned" &&
      selectedRightTeamId === selectedLeftTeamId
    ) {
      return true;
    }
    return false;
  }, [
    selectedLeftTeamId,
    selectedRightTeamId,
  ]);

  const baseLeftData = useMemo(() => {
    if (selectedLeftTeamId === "unassigned") {
      return unassignedNurses;
    }
    if (selectedLeftTeamId !== null) {
      return nursesByTeam[selectedLeftTeamId] || [];
    }
    return leftTableData;
  }, [selectedLeftTeamId, unassignedNurses, nursesByTeam, leftTableData]);

  const baseRightData = useMemo(() => {
    if (selectedRightTeamId === "unassigned") {
      return unassignedNurses;
    }
    if (typeof selectedRightTeamId === "number") {
      return nursesByTeam[selectedRightTeamId] || [];
    }
    return rightTableData;
  }, [
    selectedRightTeamId,
    nursesByTeam,
    rightTableData,
    unassignedNurses,
  ]);

  const leftTeamName = useMemo(() => {
    if (selectedLeftTeamId === "unassigned") return "미지정";
    if (selectedLeftTeamId !== null) {
      return (
        teams.find((t) => t.team_id === selectedLeftTeamId)?.team_name || "팀"
      );
    }
    return leftTableTitle;
  }, [selectedLeftTeamId, teams, leftTableTitle]);

  const rightTeamName = useMemo(() => {
    if (selectedRightTeamId === "unassigned") return "미지정";
    if (typeof selectedRightTeamId === "number") {
      return (
        teams.find((t) => t.team_id === selectedRightTeamId)?.team_name || "팀"
      );
    }
    return rightTableTitle;
  }, [selectedRightTeamId, teams, rightTableTitle]);

  const leftTeamDisplayText = useMemo(() => {
    if (selectedLeftTeamId === "unassigned") {
      return (
        <>
          미지정{" "}
          <span className="text-gray-500">({unassignedNurses.length}명)</span>
        </>
      );
    }
    if (selectedLeftTeamId !== null) {
      const teamNurses = nursesByTeam[selectedLeftTeamId] || [];
      return (
        <>
          {leftTeamName}{" "}
          <span className="text-gray-500">({teamNurses.length}명)</span>
        </>
      );
    }
    return leftTableTitle || "미지정";
  }, [
    selectedLeftTeamId,
    leftTeamName,
    unassignedNurses.length,
    nursesByTeam,
    leftTableTitle,
  ]);

  const rightTeamDisplayText = useMemo(() => {
    if (selectedRightTeamId === "unassigned") {
      return (
        <>
          미지정{" "}
          <span className="text-gray-500">
            ({unassignedNurses.length}명)
          </span>
        </>
      );
    }
    if (typeof selectedRightTeamId === "number") {
      const teamNurses = nursesByTeam[selectedRightTeamId] || [];
      return (
        <>
          {rightTeamName}{" "}
          <span className="text-gray-500">({teamNurses.length}명)</span>
        </>
      );
    }
    return rightTableTitle || "팀 선택";
  }, [
    selectedRightTeamId,
    rightTeamName,
    nursesByTeam,
    rightTableTitle,
    unassignedNurses.length,
  ]);

  const leftSelectChildren = (
    <>
      <SelectItem
        value="unassigned"
        disabled={selectedRightTeamId === "unassigned"}
      >
        <div className="flex items-center justify-between w-full">
          <span>미지정</span>
          <span className="text-gray-500 ml-2">
            ({unassignedNurses.length}명)
          </span>
        </div>
      </SelectItem>
      {sortedTeams.map((team) => {
        const teamNurses = nursesByTeam[team.team_id] || [];
        const teamCount = teamNurses.length;
        const disabled =
          typeof selectedRightTeamId === "number" &&
          team.team_id === selectedRightTeamId;
        return (
          <SelectItem
            key={team.team_id}
            value={String(team.team_id)}
            disabled={disabled}
          >
            <div className="flex items-center justify-between w-full">
              <span>{team.team_name}</span>
              <span className="text-gray-500 ml-2">({teamCount}명)</span>
            </div>
          </SelectItem>
        );
      })}
    </>
  );

  const rightSelectChildren = (
      <>
        <SelectItem
          value="unassigned"
          disabled={
            selectedLeftTeamId === "unassigned" || selectedLeftTeamId === null
          }
        >
          <div className="flex items-center justify-between w-full">
            <span>미지정</span>
            <span className="text-gray-500 ml-2">
              ({unassignedNurses.length}명)
            </span>
          </div>
        </SelectItem>
        {sortedTeams.map((team) => {
          const teamNurses = nursesByTeam[team.team_id] || [];
          const teamCount = teamNurses.length;
          const disabled =
            selectedLeftTeamId !== null &&
            selectedLeftTeamId !== "unassigned" &&
            team.team_id === selectedLeftTeamId;
          return (
            <SelectItem
              key={team.team_id}
              value={String(team.team_id)}
              disabled={disabled}
            >
              <div className="flex items-center justify-between w-full">
                <span>{team.team_name}</span>
                <span className="text-gray-500 ml-2">({teamCount}명)</span>
              </div>
            </SelectItem>
          );
        })}
      </>
    );

  const leftBucketPills = useMemo((): BucketPillOption[] => {
    const rows: BucketPillOption[] = [
      {
        value: "unassigned",
        disabled:
          selectedRightTeamId === "unassigned",
        violationTooltip: invalidTeamTooltip?.("unassigned"),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">미지정</span>
            <span className="shrink-0 tabular-nums opacity-70">
              {unassignedNurses.length}명
            </span>
          </span>
        ),
      },
    ];
    sortedTeams.forEach((team) => {
      const teamCount = nursesByTeam[team.team_id]?.length ?? 0;
      rows.push({
        value: String(team.team_id),
        disabled:
          typeof selectedRightTeamId === "number" &&
          team.team_id === selectedRightTeamId,
        violationTooltip: invalidTeamTooltip?.(team.team_id),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">
              {team.team_name}
            </span>
            <span className="shrink-0 tabular-nums opacity-70">
              {teamCount}명
            </span>
          </span>
        ),
      });
    });
    return rows;
  }, [
    selectedRightTeamId,
    unassignedNurses.length,
    sortedTeams,
    nursesByTeam,
    invalidTeamTooltip,
  ]);

  const rightBucketPills = useMemo((): BucketPillOption[] => {
    const leftIsUnassigned =
      selectedLeftTeamId === "unassigned" || selectedLeftTeamId === null;
    const rows: BucketPillOption[] = [
      {
        value: "unassigned",
        disabled: leftIsUnassigned,
        violationTooltip: invalidTeamTooltip?.("unassigned"),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">미지정</span>
            <span className="shrink-0 tabular-nums opacity-70">
              {unassignedNurses.length}명
            </span>
          </span>
        ),
      },
    ];
    sortedTeams.forEach((team) => {
      const teamCount = nursesByTeam[team.team_id]?.length ?? 0;
      rows.push({
        value: String(team.team_id),
        disabled:
          selectedLeftTeamId !== null &&
          selectedLeftTeamId !== "unassigned" &&
          team.team_id === selectedLeftTeamId,
        violationTooltip: invalidTeamTooltip?.(team.team_id),
        label: (
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <span className="min-w-0 truncate font-medium">
              {team.team_name}
            </span>
            <span className="shrink-0 tabular-nums opacity-70">
              {teamCount}명
            </span>
          </span>
        ),
      });
    });
    return rows;
  }, [
    sortedTeams,
    nursesByTeam,
    selectedLeftTeamId,
    unassignedNurses.length,
    invalidTeamTooltip,
  ]);

  return (
    <NurseSwapTables
      selectedGroupId={selectedGroupId}
      activeNurseIdForSideProfile={activeNurseIdForSideProfile}
      onNurseClick={onNurseClick}
      disableMoveRight={
        selectedRightTeamId === null || sameBucketSelected
      }
      disableMoveLeft={sameBucketSelected}
      onMoveToRight={(selectedNurseIds) => {
        if (selectedNurseIds.length === 0 || selectedRightTeamId === null)
          return;
        onMoveNurses?.(
          selectedNurseIds,
          selectedLeftTeamId,
          selectedRightTeamId
        );
      }}
      onMoveToLeft={(selectedNurseIds) => {
        if (selectedNurseIds.length === 0 || selectedRightTeamId === null)
          return;
        onMoveNurses?.(
          selectedNurseIds,
          selectedRightTeamId,
          selectedLeftTeamId
        );
      }}
      left={{
        selectValue:
          selectedLeftTeamId === "unassigned"
            ? "unassigned"
            : selectedLeftTeamId !== null
              ? String(selectedLeftTeamId)
              : "unassigned",
        onSelectChange: (value) => {
          if (value === "unassigned") {
            onLeftTeamChange("unassigned");
          } else {
            onLeftTeamChange(Number(value));
          }
        },
        triggerDisplay: leftTeamDisplayText,
        selectChildren: leftSelectChildren,
        bucketPillOptions: leftBucketPills,
        data: baseLeftData,
      }}
      right={{
        selectValue:
          selectedRightTeamId === "unassigned"
            ? "unassigned"
            : typeof selectedRightTeamId === "number"
              ? String(selectedRightTeamId)
              : "",
        onSelectChange: (value) => {
          if (value === "") {
            onRightTeamChange(null);
          } else if (value === "unassigned") {
            onRightTeamChange("unassigned");
          } else {
            onRightTeamChange(Number(value));
          }
        },
        triggerDisplay:
          selectedRightTeamId !== null ? rightTeamDisplayText : <>팀 선택</>,
        selectChildren: rightSelectChildren ?? <></>,
        bucketPillOptions: rightBucketPills,
        data: baseRightData,
      }}
      rightBucketEmpty={false}
      rightBucketEmptyHeader={
        <div className="text-muted-foreground font-xl py-3.5 font-pretendard">
          생성된 팀이 없습니다.
        </div>
      }
      rightBucketEmptyBody="팀을 생성해주세요"
    />
  );
}
